/**
 * 拍照小票适配器。
 *
 * 流程：选照片 → 本地压缩 → base64 → 服务端视觉模型 → IntakeDraft
 *
 * 两个刻意的设计：
 *   1. **本地压缩**。手机直出照片 4-8MB，base64 后更大，直接传会把服务端和
 *      运营商网络都拖死。压到长边 1600、质量 0.8，识别率几乎无损。
 *   2. **失败必降级**。视觉模型没配、调用超时、照片太糊 —— 一律返回一份
 *      指向手工录入的草稿，绝不让用户对着一个报错框发呆。
 */

import { ApiError, parseImage, type IntakeCapabilities } from '../api/client';
import { toManualFallbackDraft, type IntakeAdapter } from './types';

export const receiptAdapter: IntakeAdapter = {
  source: 'receipt',
  label: '拍小票',
  hint: '拍购物小票或冰箱内部',
  requires: 'vision',
  iconPath: 'M3 8.5A2.5 2.5 0 0 1 5.5 6h13A2.5 2.5 0 0 1 21 8.5v7a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 15.5v-7ZM8 11h.01M12 11h4M8 14h8',
};

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

/** 压缩为 JPEG data URL。失败时原样返回 data URL，宁可传大图也别丢功能。 */
export async function compressImage(file: File): Promise<string> {
  const original = await readAsDataUrl(file);

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;

    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  } catch {
    return original;
  }
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('读取图片失败'));
    reader.readAsDataURL(file);
  });
}

export async function recognizeReceipt(file: File) {
  try {
    const dataUrl = await compressImage(file);
    return await parseImage(dataUrl);
  } catch (error) {
    if (error instanceof ApiError) {
      return toManualFallbackDraft('receipt', null, `${error.message} `);
    }
    return toManualFallbackDraft(
      'receipt',
      null,
      `识别失败（${error instanceof Error ? error.message : '未知原因'}）。`,
    );
  }
}

export function receiptAvailable(caps: IntakeCapabilities | null): boolean {
  return caps?.vision === true;
}
